package app.footyguide.web.data

import kotlinx.browser.window

/**
 * localStorage cache with TTL and server `lastUpdate` version validation.
 *
 * Stores JSON string + timestamp + optional `lastUpdate` server version.
 * Automatically invalidates cached entries when the server's `lastUpdate` changes.
 */
object LocalCache {

    private const val DEFAULT_TTL_MS = 6 * 60 * 60 * 1000L // 6 hours
    private const val GLOBAL_SYNC_KEY = "fg-last-server-sync"

    /** Save a JSON string to localStorage with timestamp and optional server lastUpdate. */
    fun set(key: String, json: String, lastUpdate: String? = null) {
        try {
            val luField = if (!lastUpdate.isNullOrBlank()) {
                val escaped = JSON.stringify(lastUpdate)
                ""","lastUpdate":$escaped"""
            } else {
                ""
            }
            val entry = """{"ts":${currentTimeMs()}$luField,"data":$json}"""
            window.localStorage.setItem(key, entry)
        } catch (_: Exception) { /* Storage full or unavailable */ }
    }

    /**
     * Returns cached JSON string ONLY if its `lastUpdate` matches `serverLastUpdate`.
     * If `lastUpdate` differs, evicts the stale cache entry immediately and returns null.
     */
    fun getIfVersionMatches(key: String, serverLastUpdate: String): String? {
        if (serverLastUpdate.isBlank()) return null
        return try {
            val raw = window.localStorage.getItem(key) ?: return null
            val parsed = JSON.parse<dynamic>(raw)
            val cachedLastUpdate = parsed.lastUpdate as? String
            if (cachedLastUpdate != serverLastUpdate) {
                window.localStorage.removeItem(key)
                return null
            }
            JSON.stringify(parsed.data)
        } catch (_: Exception) {
            window.localStorage.removeItem(key)
            null
        }
    }

    /** Get cached JSON string by TTL (offline/fallback when server lastUpdate is unavailable). */
    fun get(key: String, ttlMs: Long = DEFAULT_TTL_MS): String? {
        return try {
            val raw = window.localStorage.getItem(key) ?: return null
            val parsed = JSON.parse<dynamic>(raw)
            val ts = (parsed.ts as? Number)?.toLong() ?: return null
            if (currentTimeMs() - ts >= ttlMs) {
                window.localStorage.removeItem(key)
                return null
            }
            JSON.stringify(parsed.data)
        } catch (_: Exception) { null }
    }

    /**
     * Compares `serverLastUpdate` with the last known global sync timestamp.
     * If changed, purges all `fg-cache-*` keys in localStorage and returns true.
     */
    fun syncGlobalLastUpdate(serverLastUpdate: String): Boolean {
        if (serverLastUpdate.isBlank()) return false
        return try {
            val previous = window.localStorage.getItem(GLOBAL_SYNC_KEY)
            if (previous != serverLastUpdate) {
                clearAllCaches()
                window.localStorage.setItem(GLOBAL_SYNC_KEY, serverLastUpdate)
                true
            } else {
                false
            }
        } catch (_: Exception) {
            false
        }
    }

    /** Evicts all `fg-cache-*` entries while preserving user preferences (`fg-my-team`, `fg-theme`). */
    fun clearAllCaches() {
        try {
            val storage = window.localStorage
            val keysToRemove = mutableListOf<String>()
            for (i in 0 until storage.length) {
                val k = storage.key(i)
                if (k != null && k.startsWith("fg-cache-")) {
                    keysToRemove.add(k)
                }
            }
            keysToRemove.forEach { storage.removeItem(it) }
        } catch (_: Exception) {}
    }

    /** Remove a cache entry. */
    fun remove(key: String) {
        try { window.localStorage.removeItem(key) } catch (_: Exception) {}
    }

    private fun currentTimeMs(): Long =
        (js("Date.now()") as Number).toLong()
}
